import React from 'react';
import { observer } from 'mobx-react-lite';
import Card from '../../../../components/layouts/card/card';
import FontHeading2 from '../../../../components/fonts/font-heading2/font-heading2';
import FontHeading3 from '../../../../components/fonts/font-heading3/font-heading3';
import FontBody1 from '../../../../components/fonts/font-body1/font-body1';
import { FONT_COLOR, FONT_VARIANT } from '../../../../components/fonts/font-props';
import './health-stats-card.scss';
import HealthDashboardPageStore from "../../health-dashboard-page-store";

interface IHealthStatsCardProps {
    healthStats: any;
    isLoading?: boolean;
}

interface IHealthStatsData {
    returnsCreated: number;
    returnsWithErrors: number;
    ratio: number;
}

const HealthStatsCard: React.FC<IHealthStatsCardProps> = observer(({ 
    healthStats, 
    isLoading = false 
}) => {
    const parseHealthStatsData = (data: any): IHealthStatsData | null => {
        if (!data) return null;
        
        try {
            // Handle different possible data structures
            let ratioData = data;
            ratioData = data.payload;
            
            const returnsCreated = Number(ratioData.returns_created || ratioData.returnsCreated || 0);
            const returnsWithErrors = Number(ratioData.returns_with_errors || ratioData.returnsWithErrors || 0);
            let ratio = Number(ratioData.ratio || 0);
            
            // If ratio is NaN or invalid, calculate it from the other values
            if (isNaN(ratio) || ratio === 0) {
                ratio = returnsCreated > 0 ? returnsWithErrors / returnsCreated : 0;
            }
            
            const result = {
                returnsCreated: isNaN(returnsCreated) ? 0 : returnsCreated,
                returnsWithErrors: isNaN(returnsWithErrors) ? 0 : returnsWithErrors,
                ratio: isNaN(ratio) ? 0 : ratio
            };

            return result;
        } catch (error) {
            console.error('Error parsing health ratio data:', error);
            return null;
        }
    };

    const ratioData = parseHealthStatsData(healthStats);

    const formatPercentage = (ratio: number): string => {
        return `${(ratio * 100).toFixed(1)}%`;
    };

    return (
        <Card isLoading={isLoading}>
            <div className="health-stats-card-content">
                <div className="health-stats-header">
                    <FontHeading2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY3}>
                        Overall Health
                    </FontHeading2>
                </div>
                
                {!isLoading && ratioData && (
                    <div className="health-stats">
                        <div className="stat-item">
                            <FontHeading3 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.NEUTRAL12}>
                                {ratioData.returnsCreated.toLocaleString()}
                            </FontHeading3>
                            <FontBody1 color={FONT_COLOR.NEUTRAL12}>
                                Total Returns Created
                            </FontBody1>
                        </div>
                        
                        <div className="stat-item">
                            <FontHeading3 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.NEUTRAL12}>
                                {ratioData.returnsWithErrors.toLocaleString()}
                            </FontHeading3>
                            <FontBody1 color={FONT_COLOR.NEUTRAL12}>
                                Returns With Errors
                            </FontBody1>
                        </div>
                        
                        <div className="stat-item ratio-highlight">
                            <FontHeading2 variant={FONT_VARIANT.BOLD} color={HealthDashboardPageStore.getRatioColor(ratioData.ratio)}>
                                {formatPercentage(ratioData.ratio)}
                            </FontHeading2>
                            <FontBody1 color={FONT_COLOR.NEUTRAL12}>
                                Error Rate
                            </FontBody1>
                        </div>
                    </div>
                )}
                
                {!isLoading && !ratioData && (
                    <div className="no-data-message">
                        <FontBody1 color={FONT_COLOR.NEUTRAL3}>
                            No health ratio data available
                        </FontBody1>
                    </div>
                )}
            </div>
        </Card>
    );
});

export default HealthStatsCard;
